import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Search, MapPin, Calendar, Link2, TrendingUp, Star, Globe,
  Megaphone, Target, Bell, DollarSign, Camera, Ticket,
  ArrowRight, CheckCircle2, Loader2, Building2, Mail, Phone,
  Instagram, User, Lock, Sparkles, ChevronDown,
  Music, Palette, Mic, UtensilsCrossed, Headphones,
  Compass, Landmark, GraduationCap, Wine, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Benefit = ({ icon: Icon, title, text }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-7 hover:bg-white/[0.07] transition-colors" data-testid="provider-benefit-card">
    <div className="w-12 h-12 rounded-xl bg-[#00CC66]/15 text-[#00CC66] flex items-center justify-center mb-4">
      <Icon className="w-6 h-6" strokeWidth={2} />
    </div>
    <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
    <p className="text-white/60 text-sm leading-relaxed">{text}</p>
  </div>
);

const Step = ({ n, title, text }) => (
  <div className="flex gap-5">
    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#00CC66] text-white font-black text-xl flex items-center justify-center">
      {n}
    </div>
    <div>
      <h3 className="text-[#064E3B] font-bold text-xl mb-1">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{text}</p>
    </div>
  </div>
);

const PlanCard = ({ name, price, oldPrice, period, features, highlight, tagline }) => (
  <div className={`rounded-2xl p-8 flex flex-col ${highlight ? 'bg-[#064E3B] text-white ring-4 ring-[#00CC66]/30' : 'bg-white border border-gray-200 text-[#064E3B]'}`}>
    {highlight && <Badge className="self-start mb-3 bg-[#00CC66] text-white border-0">Más popular</Badge>}
    <div className={`text-sm uppercase tracking-[3px] font-bold mb-3 ${highlight ? 'text-[#00CC66]' : 'text-gray-500'}`}>{name}</div>
    {tagline && <p className={`text-sm mb-4 ${highlight ? 'text-white/70' : 'text-gray-500'}`}>{tagline}</p>}
    <div className="flex items-baseline gap-2 mb-1 flex-wrap">
      {oldPrice && (
        <span className={`text-lg font-semibold line-through ${highlight ? 'text-white/40' : 'text-gray-400'}`}>{oldPrice}</span>
      )}
      <span className={`text-4xl font-black ${highlight ? 'text-white' : 'text-[#064E3B]'}`}>{price}</span>
      {period && <span className={`text-sm font-medium ${highlight ? 'text-white/70' : 'text-gray-500'}`}>{period}</span>}
    </div>
    {oldPrice && (
      <div className={`text-xs font-bold mb-4 ${highlight ? 'text-[#00CC66]' : 'text-[#00CC66]'}`}>
        Precio promocional de lanzamiento
      </div>
    )}
    {!oldPrice && <div className="mb-4" />}
    <ul className="space-y-3 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-2">
          <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-[#00CC66]' : 'text-[#00CC66]'}`} />
          <span className={`text-sm ${highlight ? 'text-white/90' : 'text-gray-700'}`}>{f}</span>
        </li>
      ))}
    </ul>
  </div>
);

export const ForProviders = () => {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    business_name: '',
    name: '',
    email: '',
    phone: '',
    instagram: '',
    password: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.business_name || !form.name || !form.email || !form.password) {
      setError('Por favor completa los campos obligatorios.');
      return;
    }
    if (form.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/register`, {
        email: form.email,
        password: form.password,
        name: form.name,
        role: 'provider',
        business_name: form.business_name,
        phone: form.phone || undefined,
        instagram: form.instagram || undefined,
        description: form.description || undefined,
      });
      setSuccess(true);
      toast.success('¡Registro exitoso! Un administrador verificará tu cuenta.');
      // Scroll success message into view
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al registrarse. Intenta nuevamente.';
      setError(detail === 'Email already registered' ? 'Ese email ya está registrado. Prueba iniciar sesión.' : detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="for-providers-page">
      <SEO
        title="Publica tu negocio en PlanCity - Para proveedores"
        description="Registra tu negocio o evento en PlanCity y llega a miles de personas que buscan panoramas en tu ciudad. Publicación gratis, sin comisiones."
      />

      {/* Top nav bar */}
      <header className="absolute top-0 left-0 right-0 z-20 px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="provider-landing-home-link">
          <img src="/logo_plancity_light.png" alt="PlanCity" className="h-9" />
        </Link>
        <div className="flex items-center gap-3">
          <Button
            onClick={scrollToForm}
            className="bg-[#00CC66] hover:bg-[#00b359] text-white rounded-full h-10 px-5 text-sm font-semibold"
            data-testid="provider-landing-nav-cta"
          >
            Registrarme gratis
          </Button>
        </div>
      </header>

      {/* ==== HERO ==== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#064E3B] via-[#032b21] to-[#021a14] min-h-[720px] flex items-center">
        {/* decorative glows */}
        <div className="absolute top-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.18) 0%, transparent 70%)' }} />
        <div className="absolute bottom-[-150px] left-[-150px] w-[500px] h-[500px] rounded-full"
             style={{ background: 'radial-gradient(circle, rgba(0,204,102,0.12) 0%, transparent 70%)' }} />
        {/* rainbow accent at top */}
        <div className="absolute top-0 left-0 right-0 h-1"
             style={{ background: 'linear-gradient(90deg, #E91E8B, #F7941D, #00CC66, #29ABE2)' }} />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <Badge className="bg-[#00CC66]/15 text-[#00CC66] border border-[#00CC66]/40 mb-7 px-4 py-1.5 text-xs tracking-[3px] font-bold uppercase rounded-full">
            Para proveedores, artistas y productoras
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] tracking-tight max-w-3xl mb-6">
            Publica tu negocio en <span className="text-[#00CC66]">PlanCity</span> y llega a miles de personas
          </h1>

          <p className="text-lg md:text-xl text-white/60 max-w-2xl leading-relaxed mb-10">
            Registro <strong className="text-white">100% gratis</strong>, sin comisiones, sin letra chica. Aparece en los
            resultados de búsqueda, en el mapa interactivo y recibe visitas todos los días.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-14">
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-[#00CC66] hover:bg-[#00b359] text-white rounded-full h-14 px-8 text-base font-bold shadow-lg shadow-[#00CC66]/20"
              data-testid="provider-landing-hero-cta"
            >
              Registrarme gratis <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a
              href="#como-funciona"
              className="inline-flex items-center justify-center rounded-full h-14 px-8 text-base font-bold text-white border border-white/20 hover:bg-white/5 transition-colors"
              data-testid="provider-landing-how-link"
            >
              Cómo funciona <ChevronDown className="ml-2 w-5 h-5" />
            </a>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-2xl">
            {[
              { num: '100%', lab: 'Gratis para publicar' },
              { num: '24/7', lab: 'Visible todo el día' },
              { num: '+1000', lab: 'Usuarios buscando' },
            ].map((s) => (
              <div key={s.lab} className="bg-white/[0.05] border border-white/10 rounded-2xl p-5 text-center">
                <div className="text-3xl md:text-4xl font-black text-[#00CC66] mb-1">{s.num}</div>
                <div className="text-xs md:text-sm text-white/70 font-medium">{s.lab}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==== BENEFITS ==== */}
      <section className="bg-black py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <div className="text-[#00CC66] text-xs tracking-[4px] font-bold uppercase mb-4">Por qué PlanCity</div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight">
              Mejora tu <span className="text-[#00CC66]">alcance</span> sin depender de tus redes sociales
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Benefit icon={Search} title="Te encuentran buscando panoramas" text="La gente llega a tu evento mientras busca qué hacer en la ciudad, no porque siguen tu cuenta." />
            <Benefit icon={MapPin} title="Apareces en el mapa interactivo" text="Los usuarios exploran eventos cerca de ellos con vista satelital y filtros por distancia." />
            <Benefit icon={Calendar} title="Filtros por fecha te dan visibilidad" text="Hoy, mañana, fin de semana, próximamente: aparece en el filtro correcto en el momento correcto." />
            <Benefit icon={Link2} title="Link directo a tu Instagram" text="Cada evento y tu perfil público tienen un link directo a tus redes para que te sigan." />
            <Benefit icon={TrendingUp} title="Métricas de alcance" text="Mide cuántas personas ven tu evento, cuántas hacen clic y cuántas te contactan." />
            <Benefit icon={Globe} title="SEO: aparece en Google" text="Cada evento se indexa en buscadores. Te encuentran sin que tengas que hacer publicidad pagada." />
          </div>
        </div>
      </section>

      {/* ==== HOW IT WORKS ==== */}
      <section id="como-funciona" className="bg-[#f8faf9] py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <div className="text-[#00CC66] text-xs tracking-[4px] font-bold uppercase mb-4">Cómo funciona</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#064E3B] leading-tight">
              En 3 pasos estás <span className="text-[#00CC66]">en línea</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
            <Step n="1" title="Regístrate" text="Completa el formulario con los datos de tu negocio. Un administrador revisa y verifica tu cuenta." />
            <Step n="2" title="Publica tu primer evento" text="Agrega nombre, descripción, fecha, fotos, ubicación en el mapa y link de contacto." />
            <Step n="3" title="Recibe visitas" text="Tu evento aparece en listados, mapa, carrusel y búsquedas. Compartes el link donde quieras." />
          </div>
        </div>
      </section>

      {/* ==== PLANS ==== */}
      <section className="bg-white py-20 md:py-28 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <div className="text-[#00CC66] text-xs tracking-[4px] font-bold uppercase mb-4">Planes</div>
            <h2 className="text-4xl md:text-5xl font-black text-[#064E3B] leading-tight">
              Empieza <span className="text-[#00CC66]">gratis</span>, crece cuando quieras
            </h2>
            <p className="text-gray-600 mt-4 text-lg">Todos los planes incluyen publicación de eventos. Los pagos son opcionales y vienen después.</p>
            <div className="inline-flex items-center gap-2 mt-5 bg-[#00CC66]/10 text-[#00CC66] border border-[#00CC66]/30 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[2px]">
              <Sparkles className="w-4 h-4" />
              Precios promocionales de lanzamiento
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <PlanCard
              name="Basic"
              price="Gratis"
              tagline="Para partir y probar la plataforma"
              features={[
                'Hasta 3 eventos activos',
                'Perfil público de negocio',
                'Ubicación en el mapa',
                'Aparece en búsquedas y filtros',
              ]}
            />
            <PlanCard
              name="Pro"
              price="$5.990"
              oldPrice="$9.990"
              period="/ mes"
              highlight
              tagline="Para organizadores activos"
              features={[
                'Hasta 10 eventos activos',
                'Eventos destacados en el carrusel',
                'Estadísticas de vistas y clics',
                'Galería de imágenes por evento',
                'Soporte prioritario',
              ]}
            />
            <PlanCard
              name="Premium"
              price="$19.990"
              oldPrice="$24.990"
              period="/ mes"
              tagline="Para productoras y marcas"
              features={[
                'Eventos ilimitados',
                'Badge Partner PlanCity',
                'Destacados premium',
                'Atención personalizada',
                'Acceso temprano a nuevas features',
              ]}
            />
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            Valores en pesos chilenos (CLP). Sin permanencia, cancelas cuando quieras. Puedes cambiar de plan contactando al administrador.
          </p>
        </div>
      </section>

      {/* ==== IDEAL FOR ==== */}
      <section className="bg-[#064E3B] py-20 md:py-24 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(0,204,102,0.25), transparent 50%), radial-gradient(circle at 80% 70%, rgba(0,204,102,0.15), transparent 50%)' }} />
        <div className="relative max-w-5xl mx-auto">
          <div className="text-[#00CC66] text-xs tracking-[4px] font-bold uppercase mb-4">Ideal para</div>
          <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-12 max-w-4xl">
            Si organizas algo, <span className="text-[#00CC66] whitespace-nowrap">PlanCity es para ti</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Ticket, lab: 'Productoras de eventos' },
              { icon: Megaphone, lab: 'Organizadores' },
              { icon: Compass, lab: 'Agencias de turismo' },
              { icon: Palette, lab: 'Centros culturales' },
              { icon: Music, lab: 'Artistas y bandas' },
              { icon: Headphones, lab: 'DJs y música en vivo' },
              { icon: Mic, lab: 'Teatro y stand up' },
              { icon: Landmark, lab: 'Museos y galerías' },
              { icon: GraduationCap, lab: 'Escuelas y academias' },
              { icon: Wine, lab: 'Viñas y bodegas' },
              { icon: UtensilsCrossed, lab: 'Pubs y restaurantes con shows' },
              { icon: Heart, lab: 'Fundaciones y ONG' },
            ].map((it) => (
              <div key={it.lab} className="bg-white/[0.06] border border-white/10 rounded-2xl p-5 text-center hover:bg-white/[0.09] transition-colors">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#00CC66]/20 text-[#00CC66] flex items-center justify-center">
                  <it.icon className="w-6 h-6" strokeWidth={2} />
                </div>
                <div className="text-white font-semibold text-sm">{it.lab}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==== FORM ==== */}
      <section ref={formRef} id="registro" className="bg-white py-20 md:py-28 px-6 md:px-12" data-testid="provider-registration-section">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-[#00CC66]/10 text-[#00CC66] border-0 mb-4 px-4 py-1.5 text-xs tracking-[3px] font-bold uppercase rounded-full">
              Regístrate gratis
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-[#064E3B] leading-tight mb-4">
              Tu negocio en PlanCity <br/> en <span className="text-[#00CC66]">menos de 24h</span>
            </h2>
            <p className="text-gray-600 text-lg">
              Completa el formulario. Un administrador verificará tu cuenta y quedarás activo para publicar eventos.
            </p>
          </div>

          {success ? (
            <Card className="border-2 border-[#00CC66] bg-[#f0fdf4] rounded-3xl" data-testid="provider-register-success">
              <CardContent className="p-10 text-center">
                <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-[#00CC66] flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-black text-[#064E3B] mb-3">¡Registro recibido!</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Ya creamos tu cuenta con el email <strong>{form.email}</strong>. Ya puedes iniciar sesión y comenzar a publicar en la sección <strong>Mis Eventos</strong>, para que el mundo se entere qué plan tienes para la ciudad.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/login">
                    <Button className="bg-[#064E3B] hover:bg-[#053d2f] text-white rounded-full h-12 px-6 font-bold w-full sm:w-auto" data-testid="provider-register-login-btn">
                      Iniciar sesión
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="rounded-full h-12 px-6 font-bold w-full sm:w-auto">
                      Ir al inicio
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-3xl border border-gray-200 shadow-xl">
              <CardContent className="p-8 md:p-10">
                <form onSubmit={onSubmit} className="space-y-5" data-testid="provider-register-form">
                  {error && (
                    <Alert variant="destructive" className="rounded-xl" data-testid="provider-register-error">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="business_name">Nombre del negocio / organización *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input id="business_name" name="business_name" value={form.business_name} onChange={onChange}
                             className="pl-10 rounded-xl h-12" placeholder="Ej: Cafe La Plaza" required
                             data-testid="provider-register-business-name" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre de contacto *</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="name" name="name" value={form.name} onChange={onChange}
                               className="pl-10 rounded-xl h-12" placeholder="Tu nombre" required
                               data-testid="provider-register-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="email" name="email" type="email" value={form.email} onChange={onChange}
                               className="pl-10 rounded-xl h-12" placeholder="tu@email.com" required
                               data-testid="provider-register-email" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Teléfono <span className="text-gray-400 text-xs">(opcional)</span></Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="phone" name="phone" value={form.phone} onChange={onChange}
                               className="pl-10 rounded-xl h-12" placeholder="+56 9 1234 5678"
                               data-testid="provider-register-phone" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="instagram">Instagram <span className="text-gray-400 text-xs">(opcional)</span></Label>
                      <div className="relative">
                        <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input id="instagram" name="instagram" value={form.instagram} onChange={onChange}
                               className="pl-10 rounded-xl h-12" placeholder="@tunegocio"
                               data-testid="provider-register-instagram" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input id="password" name="password" type="password" value={form.password} onChange={onChange}
                             className="pl-10 rounded-xl h-12" placeholder="Mínimo 6 caracteres" required minLength={6}
                             data-testid="provider-register-password" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Cuéntanos sobre tu negocio <span className="text-gray-400 text-xs">(opcional)</span></Label>
                    <Textarea id="description" name="description" value={form.description} onChange={onChange}
                              className="rounded-xl min-h-[90px]" placeholder="¿Qué haces? ¿Dónde estás? ¿Por qué quieres estar en PlanCity?"
                              data-testid="provider-register-description" />
                  </div>

                  <Button type="submit" disabled={loading}
                          className="w-full bg-[#00CC66] hover:bg-[#00b359] text-white rounded-full h-14 font-bold text-base shadow-lg shadow-[#00CC66]/20"
                          data-testid="provider-register-submit">
                    {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Enviando...</> : <>Registrarme gratis <ArrowRight className="ml-2 w-5 h-5" /></>}
                  </Button>

                  <p className="text-xs text-gray-500 text-center pt-2">
                    Al registrarte aceptas que un administrador revise tus datos para activar tu cuenta.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* ==== FOOTER ==== */}
      <footer className="bg-[#0a0a0a] text-white/50 py-10 px-6 md:px-12 text-center text-sm">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo_plancity_light.png" alt="PlanCity" className="h-7 opacity-80" />
            <span>© {new Date().getFullYear()} PlanCity. Todos los derechos reservados.</span>
          </div>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForProviders;
